import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, forkJoin, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import {
  VideoEntity,
  VideoLibraryEntity,
  VideoPlaybackAuthenticationModeAllItems
} from '../../../meta-data/app-meta-data.service';
import { VideoThumbnailPreview } from '../models/video-playback.model';
import { VideoRepositoryService } from './video-repository.service';

interface PendingThumbnailRequest {
  cacheKey: string;
  video: VideoEntity;
  result: ReplaySubject<string>;
}

interface CachedThumbnail {
  expiresAtUtc?: string;
  url: string;
}

@Injectable({ providedIn: 'root' })
export class VideoThumbnailPreviewService {
  private static readonly maxBatchSize = 200;
  private static readonly batchDelayMilliseconds = 25;
  private static readonly expirationBufferMilliseconds = 5 * 60 * 1000;

  private readonly cache = new Map<string, CachedThumbnail>();
  private readonly pending = new Map<string, PendingThumbnailRequest>();
  private flushScheduled = false;

  constructor(private readonly videoRepositoryService: VideoRepositoryService) { }

  getThumbnail(video: VideoEntity): Observable<string> {
    const cleanThumbnailUrl = this.cleanThumbnailUrl(video?.ThumbnailUrl);
    if (!cleanThumbnailUrl) {
      return of(null);
    }

    if (!video?.VideoId || !video.VideoLibraryId) {
      return of(cleanThumbnailUrl);
    }

    const cacheKey = this.createCacheKey(video);
    const cachedThumbnail = this.cache.get(cacheKey);
    if (cachedThumbnail && !this.isExpiring(cachedThumbnail.expiresAtUtc)) {
      return of(cachedThumbnail.url);
    }

    const pendingRequest = this.pending.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest.result.asObservable();
    }

    const result = new ReplaySubject<string>(1);
    this.pending.set(cacheKey, { cacheKey, video, result });
    this.scheduleFlush();

    return result.asObservable();
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) {
      return;
    }

    this.flushScheduled = true;
    setTimeout(() => this.flush(), VideoThumbnailPreviewService.batchDelayMilliseconds);
  }

  private flush(): void {
    this.flushScheduled = false;
    const requests = Array.from(this.pending.values());
    this.pending.clear();
    if (requests.length === 0) {
      return;
    }

    this.videoRepositoryService.getStreamingProviders()
      .pipe(take(1))
      .subscribe(
        providers => this.resolveRequests(requests, providers
          .reduce(
            (libraries, provider) => libraries.concat(provider.VideoLibrary || []),
            [] as VideoLibraryEntity[])),
        () => requests.forEach(request => this.resolveRawThumbnail(request))
      );
  }

  private resolveRequests(requests: PendingThumbnailRequest[], libraries: VideoLibraryEntity[]): void {
    const librariesById = new Map<number, VideoLibraryEntity>();
    libraries.forEach(library => librariesById.set(library.VideoLibraryId, library));

    const protectedRequests = requests.filter(request => {
      const library = librariesById.get(request.video.VideoLibraryId);
      const playbackModeId = library?.VideoPlaybackAuthenticationModeId;
      const requiresCdnToken = playbackModeId === VideoPlaybackAuthenticationModeAllItems.Enabled
        || playbackModeId === VideoPlaybackAuthenticationModeAllItems.Required;

      if (!requiresCdnToken) {
        this.resolveRawThumbnail(request);
      }

      return requiresCdnToken;
    });

    this.resolveProtectedThumbnails(protectedRequests);
  }

  private resolveProtectedThumbnails(requests: PendingThumbnailRequest[]): void {
    if (requests.length === 0) {
      return;
    }

    const requestByVideoId = new Map<number, PendingThumbnailRequest>();
    requests.forEach(request => requestByVideoId.set(request.video.VideoId, request));
    const videoIds = Array.from(requestByVideoId.keys());
    const batches = this.chunk(videoIds, VideoThumbnailPreviewService.maxBatchSize);

    forkJoin(batches.map(batch => this.videoRepositoryService.getVideoThumbnailPreviews(batch)))
      .pipe(
        map(results => results.reduce((all, result) => all.concat(result), [])),
        catchError(() => of([] as VideoThumbnailPreview[]))
      )
      .subscribe(thumbnails => {
        const thumbnailsByVideoId = new Map<number, VideoThumbnailPreview>();
        thumbnails.forEach(thumbnail => thumbnailsByVideoId.set(thumbnail.VideoId, thumbnail));

        requestByVideoId.forEach((request, videoId) => {
          const thumbnail = thumbnailsByVideoId.get(videoId);
          this.completeRequest(request, thumbnail?.ThumbnailUrl || null, thumbnail?.ExpiresAtUtc);
        });
      });
  }

  private resolveRawThumbnail(request: PendingThumbnailRequest): void {
    this.completeRequest(request, this.cleanThumbnailUrl(request.video.ThumbnailUrl));
  }

  private completeRequest(request: PendingThumbnailRequest, url: string, expiresAtUtc?: string): void {
    if (url) {
      this.cache.set(request.cacheKey, { url, expiresAtUtc });
    }

    request.result.next(url);
    request.result.complete();
  }

  private createCacheKey(video: VideoEntity): string {
    return `${video.VideoId}|${video.VideoLibraryId}|${video.ThumbnailUrl || ''}`;
  }

  private isExpiring(expiresAtUtc?: string): boolean {
    if (!expiresAtUtc) {
      return false;
    }

    return new Date(expiresAtUtc).getTime()
      <= Date.now() + VideoThumbnailPreviewService.expirationBufferMilliseconds;
  }

  private cleanThumbnailUrl(thumbnailUrl: string): string {
    if (!thumbnailUrl) {
      return null;
    }

    try {
      const parsedUrl = new URL(thumbnailUrl);
      parsedUrl.searchParams.delete('token');
      parsedUrl.searchParams.delete('token_path');
      parsedUrl.searchParams.delete('expires');

      return parsedUrl.toString();
    } catch {
      return thumbnailUrl;
    }
  }

  private chunk(videoIds: number[], size: number): number[][] {
    const chunks: number[][] = [];
    for (let index = 0; index < videoIds.length; index += size) {
      chunks.push(videoIds.slice(index, index + size));
    }

    return chunks;
  }
}
